"use client";

import { forwardRef, type ButtonHTMLAttributes } from "react";
import Image from "next/image";
import { CircleAlertIcon, ChevronDownIcon } from "lucide-react";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Markdown } from "@/components/ai-elements/markdown";
import { ToolCallFallback } from "@/components/ai-elements/tool-call";
import { Button } from "@tracify/ui/button";
import { Badge } from "@tracify/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@tracify/ui/card";
import { Separator } from "@tracify/ui/separator";
import { cn } from "@/lib/utils";
import {
  formatCapabilityLabel,
  formatEntityTypeLabel,
  formatOutputTypeLabel,
  getActionCardModel,
  getActionTitle,
  getOutputCardModel,
  getOutputTitle,
  type ActionCardModel,
  type ToolPart,
  type OutputCardModel,
} from "./tool-artifact-models";
import { ToolPhaseGroup } from "./tool-phase-group";
import {
  type PendingToolCallApproval,
  type ToolCallApprovalActionInput,
} from "../tool-call-approval";

type ToolApprovalHandlers = {
  onApproveToolCall?: (input: ToolCallApprovalActionInput) => void;
  onDenyToolCall?: (input: ToolCallApprovalActionInput) => void;
};

function hasRecordContent(value: Record<string, unknown> | null) {
  return Boolean(value && Object.keys(value).length > 0);
}

function formatCountLabel(count: number, singular: string) {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

function buildPreviewLabel(labels: string[], singular: string) {
  if (labels.length === 0) {
    return `Review ${singular}${
      singular === "output" ? "s" : "s"
    } from this step.`;
  }

  if (labels.length === 1) {
    return labels[0];
  }

  if (labels.length === 2) {
    return `${labels[0]} · ${labels[1]}`;
  }

  return `${labels[0]} · ${labels[1]} +${labels.length - 2} more`;
}

function hasArtifactOutputError(part: ToolPart) {
  if (
    !part.output ||
    typeof part.output !== "object" ||
    Array.isArray(part.output)
  ) {
    return false;
  }

  const output = part.output as Record<string, unknown>;

  return output.error === true || output.type === "error-text";
}

function getActionIdentityKey(part: ToolPart, index: number) {
  if (
    part.output &&
    typeof part.output === "object" &&
    !Array.isArray(part.output) &&
    typeof (part.output as Record<string, unknown>).action_fingerprint ===
      "string"
  ) {
    return `fingerprint:${
      (part.output as Record<string, unknown>).action_fingerprint as string
    }`;
  }

  if (
    part.input &&
    typeof part.input === "object" &&
    !Array.isArray(part.input) &&
    typeof (part.input as Record<string, unknown>).action_type === "string" &&
    typeof (part.input as Record<string, unknown>).target === "object" &&
    (part.input as Record<string, unknown>).target !== null
  ) {
    return JSON.stringify({
      action_type: (part.input as Record<string, unknown>).action_type,
      target: (part.input as Record<string, unknown>).target,
      changes:
        typeof (part.input as Record<string, unknown>).changes === "object" &&
        (part.input as Record<string, unknown>).changes !== null
          ? (part.input as Record<string, unknown>).changes
          : typeof (part.input as Record<string, unknown>).patch === "object" &&
              (part.input as Record<string, unknown>).patch !== null
            ? (part.input as Record<string, unknown>).patch
            : {},
    });
  }

  return `index:${index}`;
}

function collapseActionRetries(parts: ToolPart[]) {
  const lastByIdentity = new Map<string, ToolPart>();
  const orderedKeys: string[] = [];

  parts.forEach((part, index) => {
    const identity = getActionIdentityKey(part, index);
    if (!lastByIdentity.has(identity)) {
      orderedKeys.push(identity);
    }
    lastByIdentity.set(identity, part);
  });

  return orderedKeys
    .map((identity) => lastByIdentity.get(identity))
    .filter((part): part is ToolPart => Boolean(part));
}

function getArtifactToolState(
  part: ToolPart,
  activeLabel: string,
  failedDescription: string,
  approvalData?: PendingToolCallApproval | null
) {
  if (part.state === "output-error") {
    return {
      label: "Failed",
      tone: "destructive" as const,
      description: failedDescription,
    };
  }

  if (
    part.state === "output-denied" ||
    (part.state === "approval-responded" && part.approval?.approved === false)
  ) {
    return {
      label: "Failed",
      tone: "destructive" as const,
      description: failedDescription,
    };
  }

  if (hasArtifactOutputError(part)) {
    return {
      label: "Failed",
      tone: "destructive" as const,
      description: failedDescription,
    };
  }

  if (part.state === "approval-requested" || approvalData != null) {
    return {
      label: "Awaiting tool approval",
      tone: "outline" as const,
      description: "Approve this tool call to continue.",
    };
  }

  return {
    label: activeLabel,
    tone: "secondary" as const,
    description: null,
  };
}

function isFailedArtifactPart(part: ToolPart) {
  return (
    part.state === "output-error" ||
    part.state === "output-denied" ||
    (part.state === "approval-responded" &&
      part.approval?.approved === false) ||
    hasArtifactOutputError(part)
  );
}

const ArtifactSummaryCard = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    title: string;
    count: number;
    preview: string;
    isActive: boolean;
    isOpen: boolean;
  }
>(function ArtifactSummaryCard(
  { title, count, preview, isActive, isOpen, className, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn("w-full text-left", className)}
      {...props}
    >
      <Card className="transition-colors hover:bg-muted/40">
        <CardHeader className="gap-2 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-sm">
                  {isActive ? <Shimmer>{title}</Shimmer> : title}
                </CardTitle>
                <Badge variant="secondary">{count}</Badge>
              </div>
              <CardDescription className="truncate">{preview}</CardDescription>
            </div>
            <ChevronDownIcon
              className={cn(
                "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </CardHeader>
      </Card>
    </button>
  );
});

function ArtifactMeta({ revision }: { revision: number | null }) {
  if (!revision) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span>Revision {revision}</span>
    </div>
  );
}

function ArtifactStatusCard({
  title,
  description,
  metadata,
  handlers,
  part,
  approvalData,
  isToolApprovalPending = false,
}: {
  title: string;
  description: string | null;
  metadata?: string | null;
  handlers: ToolApprovalHandlers;
  part: ToolPart;
  approvalData?: PendingToolCallApproval | null;
  isToolApprovalPending?: boolean;
}) {
  const showApprovalActions =
    part.state === "approval-requested" && approvalData != null;

  return (
    <Card>
      <CardHeader className="gap-2 p-3">
        <CardTitle className="text-sm">{title}</CardTitle>
        {metadata || description ? (
          <CardDescription>
            {[metadata, description].filter(Boolean).join(" · ")}
          </CardDescription>
        ) : null}
      </CardHeader>

      {showApprovalActions ? (
        <CardFooter className="gap-2 p-3 pt-0">
          {isToolApprovalPending ? (
            <div className="text-xs text-muted-foreground">Processing…</div>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                disabled={!handlers.onApproveToolCall}
                onClick={() =>
                  handlers.onApproveToolCall?.({
                    approvalId: approvalData!.approvalId,
                  })
                }
              >
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!handlers.onDenyToolCall}
                onClick={() =>
                  handlers.onDenyToolCall?.({
                    approvalId: approvalData!.approvalId,
                  })
                }
              >
                Deny
              </Button>
            </>
          )}
        </CardFooter>
      ) : null}
    </Card>
  );
}

function ArtifactFailureRow({
  title,
  description,
}: {
  title: string;
  description: string | null;
}) {
  return (
    <div className="flex items-start gap-2 rounded-md px-1 py-1 text-sm text-muted-foreground">
      <CircleAlertIcon
        className="mt-0.5 size-4 shrink-0 text-destructive"
        aria-hidden="true"
      />
      <div className="min-w-0">
        <span className="font-medium text-foreground/80">{title}</span>
        {description ? <span>{` · ${description}`}</span> : null}
      </div>
    </div>
  );
}

function MediaBuyingOutputCard({ model }: { model: OutputCardModel }) {
  if (model.kind === "image-generation") {
    return (
      <Card>
        <CardHeader className="gap-2 p-3">
          <CardTitle className="text-sm">{model.title}</CardTitle>
          <CardDescription>Generated image</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 p-3 pt-0">
          {model.text ? (
            <div className="text-sm">
              <Markdown>{model.text}</Markdown>
            </div>
          ) : null}
          <div
            className={cn(
              "grid gap-3",
              model.images.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"
            )}
          >
            {model.images.map((image, index) => (
              <div
                key={`${image.url}-${index}`}
                className="overflow-hidden rounded-lg border border-border bg-muted/20"
              >
                <Image
                  src={image.url}
                  alt={
                    model.prompt
                      ? `${model.prompt} (${index + 1})`
                      : `${model.title} (${index + 1})`
                  }
                  width={1024}
                  height={1024}
                  unoptimized
                  className="h-auto w-full object-cover"
                />
              </div>
            ))}
          </div>
          {model.prompt ? (
            <>
              <Separator />
              <div className="flex flex-col gap-1">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Prompt
                </div>
                <div className="text-sm text-muted-foreground">
                  {model.prompt}
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (model.kind === "markdown") {
    return (
      <Card>
        <CardHeader className="gap-2 p-3">
          <CardTitle className="text-sm">{model.title}</CardTitle>
          <ArtifactMeta revision={model.revision} />
        </CardHeader>
        <CardContent className="flex flex-col gap-3 p-3 pt-0 text-sm">
          <Markdown>{model.bodyMarkdown}</Markdown>
          {hasRecordContent(model.data) ? (
            <>
              <Separator />
              <div className="flex flex-col gap-1">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Structured data
                </div>
                <ToolCallFallback value={model.data} />
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (model.kind === "generic") {
    return (
      <Card>
        <CardHeader className="gap-2 p-3">
          <CardTitle className="text-sm">{model.title}</CardTitle>
          <ArtifactMeta revision={model.revision} />
        </CardHeader>
        <CardContent className="flex flex-col gap-3 p-3 pt-0">
          <div className="flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Input
            </div>
            <ToolCallFallback value={model.input} />
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Output
            </div>
            <ToolCallFallback value={model.output} />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="gap-2 p-3">
        <CardTitle className="text-sm">{model.title}</CardTitle>
        <ArtifactMeta revision={model.revision} />
        <CardDescription>
          {formatOutputTypeLabel(model.objectType)}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-3 pt-0">
        {model.bodyMarkdown ? (
          <div className="text-sm">
            <Markdown>{model.bodyMarkdown}</Markdown>
          </div>
        ) : null}
        {hasRecordContent(model.data) ? (
          <>
            {model.bodyMarkdown ? <Separator /> : null}
            <div className="flex flex-col gap-1">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Structured data
              </div>
              <ToolCallFallback value={model.data} />
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MediaBuyingActionCard({ model }: { model: ActionCardModel }) {
  if (model.kind === "generic") {
    return (
      <Card>
        <CardHeader className="gap-2 p-3">
          <CardTitle className="text-sm">{model.title}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 p-3 pt-0">
          <div className="flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Input
            </div>
            <ToolCallFallback value={model.input} />
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Output
            </div>
            <ToolCallFallback value={model.output} />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="gap-2 p-3">
        <CardTitle className="text-sm">{model.title}</CardTitle>
        {model.summary ? (
          <CardDescription>{model.summary}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-3 pt-0">
        <div className="text-xs text-muted-foreground">
          {[
            formatCapabilityLabel(model.actionType),
            model.target.entityName ??
              formatEntityTypeLabel(model.target.entityType),
            model.target.platform
              ? formatCapabilityLabel(model.target.platform)
              : null,
            model.capabilityKey
              ? formatCapabilityLabel(model.capabilityKey)
              : null,
            model.status ? formatCapabilityLabel(model.status) : null,
            model.deduped ? "Already pending" : null,
            model.requiresApproval ? "Approval required" : null,
            typeof model.confidence === "number"
              ? `${Math.round(model.confidence * 100)}% confidence`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Target
          </div>
          <ToolCallFallback value={model.target} />
        </div>
        {hasRecordContent(model.changes) ? (
          <div className="flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Changes
            </div>
            <ToolCallFallback value={model.changes} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function OutputToolCard({
  part,
  approvalData,
  isToolApprovalPending = false,
  ...handlers
}: {
  part: ToolPart;
  approvalData?: PendingToolCallApproval | null;
  isToolApprovalPending?: boolean;
} & ToolApprovalHandlers) {
  if (isFailedArtifactPart(part)) {
    const state = getArtifactToolState(
      part,
      "Saving output",
      part.type === "tool-generateImage"
        ? "Failed to generate image."
        : "Failed to save output.",
      approvalData
    );

    return (
      <ArtifactFailureRow
        title={getOutputTitle(part)}
        description={state.description}
      />
    );
  }

  if (part.state !== "output-available") {
    const state = getArtifactToolState(
      part,
      "Saving output",
      part.type === "tool-generateImage"
        ? "Failed to generate image."
        : "Failed to save output.",
      approvalData
    );
    const objectType =
      typeof part.input === "object" &&
      part.input !== null &&
      "object_type" in part.input &&
      typeof part.input.object_type === "string"
        ? part.input.object_type
        : null;
    const outputLabel =
      part.type === "tool-generateImage"
        ? "Generated image"
        : objectType
          ? formatOutputTypeLabel(objectType)
          : null;

    return (
      <ArtifactStatusCard
        title={getOutputTitle(part)}
        description={state.description}
        metadata={[outputLabel, state.label].filter(Boolean).join(" · ")}
        part={part}
        approvalData={approvalData}
        isToolApprovalPending={isToolApprovalPending}
        handlers={handlers}
      />
    );
  }

  return <MediaBuyingOutputCard model={getOutputCardModel(part)} />;
}

function ActionToolCard({
  part,
  approvalData,
  isToolApprovalPending = false,
  ...handlers
}: {
  part: ToolPart;
  approvalData?: PendingToolCallApproval | null;
  isToolApprovalPending?: boolean;
} & ToolApprovalHandlers) {
  if (isFailedArtifactPart(part)) {
    const state = getArtifactToolState(
      part,
      "Proposing action",
      "Failed to propose action.",
      approvalData
    );

    return (
      <ArtifactFailureRow
        title={getActionTitle(part)}
        description={state.description}
      />
    );
  }

  if (part.state !== "output-available") {
    const state = getArtifactToolState(
      part,
      "Proposing action",
      "Failed to propose action.",
      approvalData
    );
    const capabilityKey =
      typeof part.input === "object" &&
      part.input !== null &&
      "capability_key" in part.input &&
      typeof part.input.capability_key === "string"
        ? part.input.capability_key
        : null;

    return (
      <ArtifactStatusCard
        title={getActionTitle(part)}
        description={state.description}
        metadata={[
          capabilityKey ? formatCapabilityLabel(capabilityKey) : null,
          state.label,
        ]
          .filter(Boolean)
          .join(" · ")}
        part={part}
        approvalData={approvalData}
        isToolApprovalPending={isToolApprovalPending}
        handlers={handlers}
      />
    );
  }

  return <MediaBuyingActionCard model={getActionCardModel(part)} />;
}

export function OutputToolGroup({
  parts,
  isActive = false,
  approvalDataByToolCallId,
  pendingToolApprovalId,
  onApproveToolCall,
  onDenyToolCall,
}: {
  parts: ToolPart[];
  isActive?: boolean;
  approvalDataByToolCallId?: Map<string, PendingToolCallApproval>;
  pendingToolApprovalId?: string | null;
} & ToolApprovalHandlers) {
  const nonFailedParts = parts.filter((part) => !isFailedArtifactPart(part));
  const preview = buildPreviewLabel(
    Array.from(
      new Set(nonFailedParts.map(getOutputTitle).filter(Boolean))
    ).slice(0, 3),
    "output"
  );

  return (
    <ToolPhaseGroup
      defaultOpen={isActive}
      renderTrigger={({ isOpen }) => (
        <ArtifactSummaryCard
          title={isActive ? "Saving outputs" : "Saved outputs"}
          count={nonFailedParts.length}
          preview={`${formatCountLabel(
            nonFailedParts.length,
            "output"
          )} · ${preview}`}
          isActive={isActive}
          isOpen={isOpen}
        />
      )}
    >
      <div className="flex flex-col gap-3">
        {parts.map((part, index) => (
          <OutputToolCard
            key={`${part.toolCallId ?? part.type}-${index}`}
            part={part}
            approvalData={
              approvalDataByToolCallId?.get(part.toolCallId) ?? null
            }
            isToolApprovalPending={
              approvalDataByToolCallId?.has(part.toolCallId) &&
              pendingToolApprovalId ===
                approvalDataByToolCallId.get(part.toolCallId)?.approvalId
            }
            onApproveToolCall={onApproveToolCall}
            onDenyToolCall={onDenyToolCall}
          />
        ))}
      </div>
    </ToolPhaseGroup>
  );
}

export function ActionToolGroup({
  parts,
  isActive = false,
  approvalDataByToolCallId,
  pendingToolApprovalId,
  onApproveToolCall,
  onDenyToolCall,
}: {
  parts: ToolPart[];
  isActive?: boolean;
  approvalDataByToolCallId?: Map<string, PendingToolCallApproval>;
  pendingToolApprovalId?: string | null;
} & ToolApprovalHandlers) {
  const collapsedParts = collapseActionRetries(parts);
  const nonFailedParts = collapsedParts.filter(
    (part) => !isFailedArtifactPart(part)
  );
  const preview = buildPreviewLabel(
    Array.from(
      new Set(nonFailedParts.map(getActionTitle).filter(Boolean))
    ).slice(0, 3),
    "action"
  );

  return (
    <ToolPhaseGroup
      defaultOpen={isActive}
      renderTrigger={({ isOpen }) => (
        <ArtifactSummaryCard
          title={isActive ? "Proposing actions" : "Proposed actions"}
          count={nonFailedParts.length}
          preview={`${formatCountLabel(
            nonFailedParts.length,
            "action"
          )} · ${preview}`}
          isActive={isActive}
          isOpen={isOpen}
        />
      )}
    >
      <div className="flex flex-col gap-3">
        {collapsedParts.map((part, index) => (
          <ActionToolCard
            key={`${part.toolCallId ?? part.type}-${index}`}
            part={part}
            approvalData={
              approvalDataByToolCallId?.get(part.toolCallId) ?? null
            }
            isToolApprovalPending={
              approvalDataByToolCallId?.has(part.toolCallId) &&
              pendingToolApprovalId ===
                approvalDataByToolCallId.get(part.toolCallId)?.approvalId
            }
            onApproveToolCall={onApproveToolCall}
            onDenyToolCall={onDenyToolCall}
          />
        ))}
      </div>
    </ToolPhaseGroup>
  );
}
